import { FC, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { LinkIconPositionsEnum, LoadingStatuses } from '@argenta/react-ui';
import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { t } from '@lingui/macro';

import { ApplicationForm, ApplicationFormValues } from '../../components/Forms';
import { I18nLinkProps } from '../../components/I18nLink/I18nLink.types';
import { VacancyPageTemplate } from '../../components/VacancyPageTemplate';
import { usePostVacancyApplication } from '../../hooks/api/usePostVacancyApplication';
import { useAutoRedirect } from '../../hooks/navigation/useAutoRedirect';
import { useI18nNavigate } from '../../hooks/navigation/useI18nNavigate';
import { VacanciesRoute } from '../../routers/AppRouter/routes';

export const VacancyApplication: FC = () => {
  const { state } = useLocation();
  const { loadingStatus, postVacancyApplication, vacancyApplicationError } =
    usePostVacancyApplication({
      immediateExecute: false,
    });

  const redirectCondition = useCallback(() => !state?.jobId, [state?.jobId]);
  useAutoRedirect(VacanciesRoute.SEARCH_VACANCIES, redirectCondition);
  const navigate = useI18nNavigate();

  const backToDetailsLink: I18nLinkProps = {
    children: t({ id: 'vacancies.application.backToDetails' }),
    icon: faChevronLeft,
    iconPosition: LinkIconPositionsEnum.LEFT,
    to: { pathname: VacanciesRoute.VACANCY_DETAILS, queryParams: { job: state?.jobId } },
  };

  const handleOnVacancySubmit = async (formData: ApplicationFormValues): Promise<void> => {
    if (loadingStatus !== LoadingStatuses.LOADING) {
      try {
        await postVacancyApplication({
          requestData: {
            vacancyId: state.jobId,
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            linkedInProfile: formData.linkedIn || '',
            file: formData.cv,
            motivation: formData.motivation,
            cvAgreement: formData.cvAgreement,
          },
        });

        navigate(VacanciesRoute.APPLICATION_CONFIRMATION);
      } catch {
        // Error via prop from hook
      }
    }
  };

  return (
    <VacancyPageTemplate
      title={
        state?.title
          ? `${t({ id: 'vacancies.application.title.prefix' })} ${state.title}`
          : undefined
      }
      contentIsForm
      returnLinkTop={backToDetailsLink}
    >
      <ApplicationForm
        onSubmit={handleOnVacancySubmit}
        errorMessage={vacancyApplicationError}
        isLoading={loadingStatus === LoadingStatuses.LOADING}
      />
    </VacancyPageTemplate>
  );
};
